/**
 * Fluid engineering properties and material compatibility data.
 *
 * Units (SI):
 *   density (ρ):   kg/m³
 *   viscosity (μ):  Pa·s  (dynamic viscosity)
 *
 * All values at approximately 20 °C unless noted.
 *
 * compatibleMaterials uses the exact strings from config.json catalog:
 *   "PVC", "Brass", "Copper", "Steel", "Stainless Steel"
 */

export const fluids = {
  water: {
    name: "Water",
    density: 998.2,
    viscosity: 1.002e-3,
    foodGrade: false,
    compatibleMaterials: ["PVC", "Brass", "Copper", "Steel", "Stainless Steel"]
  },
  sulfuricAcid10: {
    name: "Sulfuric Acid (10%)",
    density: 1066,
    viscosity: 1.2e-3,
    foodGrade: false,
    compatibleMaterials: ["PVC", "Stainless Steel"]
  },
  ethyleneGlycol30: {
    name: "Ethylene Glycol (30%)",
    density: 1038,
    viscosity: 2.0e-3,
    foodGrade: false,
    compatibleMaterials: ["PVC", "Brass", "Copper", "Steel", "Stainless Steel"]
  },
  milk: {
    name: "Milk",
    density: 1030,
    viscosity: 2.0e-3,
    foodGrade: true,
    compatibleMaterials: ["Stainless Steel"]
  },
  seawater: {
    name: "Seawater",
    density: 1025,
    viscosity: 1.08e-3,
    foodGrade: false,
    compatibleMaterials: ["PVC", "Stainless Steel"]
  }
};

/**
 * Look up a fluid by its key.
 * @param {string} key – e.g. "water", "sulfuricAcid10"
 * @returns {object|undefined}
 */
export function getFluid(key) {
  return fluids[key];
}

/**
 * Check whether a pipe/fitting material is compatible with the scenario fluid.
 * @param {string} fluidKey  – key into the fluids map
 * @param {string} material  – material string from the catalog component
 * @returns {boolean}
 */
export function isMaterialCompatible(fluidKey, material) {
  const fluid = fluids[fluidKey];
  if (!fluid) return false;
  return fluid.compatibleMaterials.includes(material);
}

/**
 * Return all incompatible materials for a given fluid (useful for UI hints).
 * @param {string} fluidKey
 * @param {string[]} allMaterials – the full set of materials in the catalog
 * @returns {string[]}
 */
export function getIncompatibleMaterials(fluidKey, allMaterials) {
  const fluid = fluids[fluidKey];
  if (!fluid) return allMaterials;
  return allMaterials.filter((m) => !fluid.compatibleMaterials.includes(m));
}
